import React from 'react';
import { EmailStatus, TimelineStatus, TimelineStepType } from '../../features/email-tracking/email-tracking.types';

/**
 * Inline SVG icons for the email-tracking timeline steps and status badges.
 * currentColor is used throughout so the consuming CSS controls the colour.
 */
type IconProps = { size?: number };

export const SentIcon: React.FC<IconProps> = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="m22 2-7 20-4-9-9-4 20-7Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
);

export const DeliveredIcon: React.FC<IconProps> = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
        <path d="m3 7 9 6 9-6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
);

export const OpenedIcon: React.FC<IconProps> = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M3 9.5 12 3l9 6.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9.5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="m3 9.5 9 6 9-6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
);

/** Invoice-progress step: salary created (document with a plus). */
export const SalaryCreatedIcon: React.FC<IconProps> = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M12 12v6m-3-3h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

/** Invoice-progress step: paid (check inside a circle). */
export const PaidIcon: React.FC<IconProps> = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
        <path d="m8.5 12 2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const BouncedIcon: React.FC<IconProps> = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const FailedIcon: React.FC<IconProps> = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
        <path d="m15 9-6 6m0-6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

export const NotSentIcon: React.FC<IconProps> = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
        <path d="m3 7 9 6 9-6" stroke="currentColor" strokeWidth="2" />
    </svg>
);

export const SignedIcon: React.FC<IconProps> = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path
            d="M4 17c2.5 0 3-6 5-6s1.5 4 3 4 2-3 3.5-3 1.5 2 2.5 2"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="M4 21h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

export const CloseIcon: React.FC<IconProps> = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

export const ChevronDownIcon: React.FC<IconProps> = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const ChevronUpIcon: React.FC<IconProps> = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="m6 15 6-6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

/** Icon for a given email status (used by the column badge). */
export const EmailStatusIcon: React.FC<{ status: EmailStatus; size?: number }> = ({ status, size }) => {
    switch (status) {
        case 'opened': return <OpenedIcon size={size} />;
        case 'delivered': return <DeliveredIcon size={size} />;
        case 'sent': return <SentIcon size={size} />;
        case 'bounced': return <BouncedIcon size={size} />;
        case 'failed': return <FailedIcon size={size} />;
        case 'not_sent':
        default: return <NotSentIcon size={size} />;
    }
};

/** Icon for the tracking-modal header pill (email statuses + invoice/document stages). */
export const TimelineStatusIcon: React.FC<{ status: TimelineStatus; size?: number }> = ({ status, size }) => {
    switch (status) {
        case 'paid': return <PaidIcon size={size} />;
        case 'salary_created': return <SalaryCreatedIcon size={size} />;
        case 'salary_paid': return <PaidIcon size={size} />;
        case 'signed': return <SignedIcon size={size} />;
        case 'declined':
        case 'cancelled':
        case 'expired':
            return <FailedIcon size={size} />;
        default: return <EmailStatusIcon status={status} size={size} />;
    }
};

/** Icon for a given timeline step node. */
export const StepIcon: React.FC<{ type: TimelineStepType; size?: number }> = ({ type, size = 20 }) => {
    switch (type) {
        case 'sent': return <SentIcon size={size} />;
        case 'delivered': return <DeliveredIcon size={size} />;
        case 'opened': return <OpenedIcon size={size} />;
        case 'paid': return <PaidIcon size={size} />;
        case 'salaryCreated': return <SalaryCreatedIcon size={size} />;
        case 'salaryPaid': return <PaidIcon size={size} />;
        case 'signed': return <SignedIcon size={size} />;
        default: return <SentIcon size={size} />;
    }
};
